import { Box, Card, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDTypography from "components/MDTypography";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import { capitalCase } from "change-case";
import { useEffect, useRef, useState } from "react";
import { kebabCase } from "lodash";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import DataTable from "components/Tables/DataTable/DataTable";
import dayjs from "dayjs";
import useReportTableOrganizationProfitability from "hooks/organization/reports/types/useReportTableOrganizationProfitability";
import OrganizationProductTypeIdsOptions from "modules/OrganizationProductTypes/OrganizationProductTypeIdsOptions/OrganizationProductTypeIdsOptions";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";

type OrganizationProfitabilityFilterFormData = {
  productTypeId: string;
};

const ReportOrganizationProfitability = () => {
  const today = dayjs().format("YYYY-MM-DD 00:00:00");
  const startDateFormatted = dayjs().subtract(1, "month").format("YYYY-MM-DD 00:00:00");

  const methods = useForm<OrganizationProfitabilityFilterFormData>({
    defaultValues: {
      productTypeId: "",
    },
  });

  const [date, setDate] = useState({ startDate: startDateFormatted, endDate: today });
  const [productTypeId, setProductTypeId] = useState("");
  const [generateReport, { data: generateReportData }] = useGenerateReport();
  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);

  const { tableData, pagination, getReportOrganizationProfitability } =
    useReportTableOrganizationProfitability(date.startDate, date.endDate, productTypeId);

  const downloadFileRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (generateReportData?.generateReport && downloadFileRef.current && requestData) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        generateReportData.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [generateReportData, requestData]);

  const changeDate = async (selectedDate: Array<Date>, type) => {
    const formattedDate = dayjs(selectedDate[0]).format("YYYY-MM-DD 00:00:00");
    setDate((previous) => ({ ...previous, [type]: formattedDate }));
  };

  const onSubmit = () => {
    getReportOrganizationProfitability({ page: 1, first: 25 });
  };

  const handleDownload = () => {
    const variables = {
      type: ReportType.ORGANIZATION_PROFITABILITY,
      ...date,
      productTypeId: productTypeId || null,
    };
    setRequestData(variables);
    generateReport({ variables });
  };

  return (
    <Box
      sx={{
        "& .css-1h9h5al-MuiAutocomplete-root .MuiOutlinedInput-root": {
          padding: "5px !important",
        },
      }}
    >
      <DashboardPage>
        <FormProvider {...methods}>
          <MDBox mb={6} component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card>
                  <MDBox
                    p={3}
                    lineHeight={1}
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box>
                      <MDTypography variant="h4" fontWeight="medium">
                        {capitalCase(ReportType.ORGANIZATION_PROFITABILITY)} Report
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mt={1}>
                        Review proposed versus actual direct costs and profit by product type
                      </MDTypography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "info.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "#fff" }}>paid</Icon>
                    </Box>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      Filter Options
                    </MDTypography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={4}>
                        <MDBox height="100%" display="flex" flexDirection="column" justifyContent="flex-end">
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Start Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={startDateFormatted}
                            onChange={(val) => changeDate(val, "startDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox height="100%" display="flex" flexDirection="column" justifyContent="flex-end">
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            End Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={today}
                            onChange={(val) => changeDate(val, "endDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox height="100%" display="flex" flexDirection="column" justifyContent="flex-end">
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Product Type
                          </MDTypography>
                          <OrganizationProductTypeIdsOptions sortByName>
                            {({ organizationProductTypeIdsOptions, loading: productTypeLoading }) => (
                              <Controller
                                name="productTypeId"
                                control={methods.control}
                                render={({ field }) => (
                                  <ScrollAsyncAutocomplete
                                    {...field}
                                    value={
                                      organizationProductTypeIdsOptions?.find((x) => x.value === field.value) || null
                                    }
                                    options={organizationProductTypeIdsOptions ?? []}
                                    loading={productTypeLoading}
                                    label="Product Type"
                                    search={() => {}}
                                    onLoadMore={() => {}}
                                    hasMore={false}
                                    onChange={(value) => {
                                      field.onChange(value || null);
                                      setProductTypeId(value ? String(value) : "");
                                    }}
                                  />
                                )}
                              />
                            )}
                          </OrganizationProductTypeIdsOptions>
                        </MDBox>
                      </Grid>
                    </Grid>
                    <MDBox mt={3} display="flex" gap={2}>
                      <MDButton variant="gradient" color="info" type="submit">
                        Get Report
                      </MDButton>
                      <a ref={downloadFileRef} className="d-none" />
                      <MDButton variant="outlined" color="success" onClick={handleDownload}>
                        Download
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <DataTable
                      canSearch={false}
                      table={tableData}
                      showTotalEntries={true}
                      isSorted={true}
                      pagination={pagination}
                      paginationProps={{ variant: "gradient", color: "info" }}
                      entriesPerPageOptions={["25", "50"]}
                    />
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </FormProvider>
      </DashboardPage>
    </Box>
  );
};

export default ReportOrganizationProfitability;
